import { Header } from "antd/es/layout/layout";
import React, { useContext, useEffect, useState, useCallback } from "react";
import { Avatar, Dropdown, Menu, MenuProps, Badge, Button, Modal } from "antd";
import { ShoppingOutlined } from "@ant-design/icons";
import { AuthContext } from "../../config/AuthContext";
import { CheckOutlined, HomeMax, LogoutOutlined, Person } from "@mui/icons-material";
import { BsBackpack, BsDatabase, BsPerson, BsChatDots, BsBook, BsBell, BsLightbulb, BsCodeSquare, BsShieldCheck } from "react-icons/bs";
import { RiJavascriptFill, RiGamepadFill } from "react-icons/ri";
import { useNavigate, useLocation } from "react-router-dom";
import HomeColors from "../../styles/HomeColors";
import AvaluacioColors from "../../styles/AvaluacioColors";
import GlobalChatNotification from "./GlobalChatNotification";
import AdminSugerimentsModal from "./AdminSugerimentsModal";
import { CountUnreadSugeriments } from "../../dao/jutge/CountUnreadSugeriments";
import UseWebSocketSubscription from "../UseWebSocketSubscription";
import UserProfileMenu from "./UserProfileMenu";

const AppHeader = () => {
    const { isLoggedIn, userPermisos, alumne, user } = useContext(AuthContext);
    const titles: string[] = ['Home', 'Base de Dades', 'Jutge Online', 'Projectes', 'Metadata'];
    const paths: string[] = ['/home', '/database', '/jutge', '/abp', '/metadata'];
    const icons: any[] = [HomeMax, BsDatabase, RiJavascriptFill, CheckOutlined, BsCodeSquare];

    const navigate = useNavigate();
    const location = useLocation();
    const [adminSugerimentsVisible, setAdminSugerimentsVisible] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    const isAdmin = user?.tipus === "1" || user?.tipus === "0";
    const isImpersonating = !!localStorage.getItem('adminToken');

    const fetchCount = useCallback(async () => {
        const c = await CountUnreadSugeriments();
        setUnreadCount(c);
    }, []);

    const onWebSocketMessage = useCallback(() => {
        if (isAdmin) fetchCount();
    }, [isAdmin, fetchCount]);

    useEffect(() => {
        if (isAdmin) fetchCount();
    }, [isAdmin, fetchCount]);

    UseWebSocketSubscription(['/topic/sugerencias'], onWebSocketMessage);

    type MenuItem = Required<MenuProps>['items'][number];

    // ¿Alguna aula del alumno tiene prácticas de Jutge Online? (contenido real, no solo permiso)
    const aulesAlumne = (alumne as any)?.aules || (alumne as any)?.alumne?.aules || [];
    const hasJutgeContent = Array.isArray(aulesAlumne) && aulesAlumne.some((a: any) => Array.isArray(a.jutge) && a.jutge.length > 0);
    // ¿Alguna aula del alumno tiene contenido de Base de Dades (activitats/exàmens bbdd)?
    const hasBbddContent = Array.isArray(aulesAlumne) && aulesAlumne.some((a: any) =>
        (Array.isArray(a.bbdd) && a.bbdd.length > 0) || Number(a.bbdd) > 0
    );

    const items1: MenuItem[] = icons.map((icon, index) => {
        const key = String(index + 1);

        let hasPermission = userPermisos.some((valor: any) => valor.accio === paths[index]);
        // La pestaña "Jutge Online" también se muestra si el usuario está en un aula
        // que tiene prácticas de Jutge Online, aunque no tenga el permiso explícito.
        if (paths[index] === '/jutge' && hasJutgeContent) hasPermission = true;
        // La pestaña "Base de Dades" también se muestra si el alumno tiene contenido bbdd
        // en alguna aula, aunque no tenga el permiso explícito.
        if (paths[index] === '/database' && hasBbddContent) hasPermission = true;
        if (!hasPermission && !paths[index].includes('godot')) return null;

        return {
            key: key,
            icon: React.createElement(icon),
            label: titles[index],
            onClick: () => {
                if (paths[index].includes('godot')) {
                    window.open(paths[index], '_blank');
                } else if (paths[index] === '/jutge') {
                    const isExamMode = user?.tipus === "2" && Array.isArray(aulesAlumne) && aulesAlumne.some((a: any) =>
                        Boolean(a.isExamen === 1 || a.isExamen === true || a.is_examen === 1 || a.is_examen === true)
                    );
                    navigate(isExamMode ? '/jutge/examens' : '/jutge');
                } else {
                    navigate(paths[index]);
                }
            },
        };
    }).filter(Boolean);



    const handleStopImpersonation = () => {
        const adminToken = localStorage.getItem('adminToken');
        if (adminToken) {
            localStorage.setItem('authToken', adminToken);
            localStorage.setItem('userId', localStorage.getItem('admin_userId') || '');
            localStorage.setItem('user', localStorage.getItem('admin_user') || '');
            localStorage.setItem('userPermisos', localStorage.getItem('admin_userPermisos') || '');
            localStorage.setItem('alumneInfo', localStorage.getItem('admin_alumneInfo') || '');

            localStorage.removeItem('adminToken');
            localStorage.removeItem('admin_userId');
            localStorage.removeItem('admin_user');
            localStorage.removeItem('admin_userPermisos');
            localStorage.removeItem('admin_alumneInfo');

            window.location.reload();
        }
    };

    // Determine active menu item
    // paths: ['/home', '/database', '/jutge', '/abp', '/metadata']
    const activeIndex = paths.findIndex(path => location.pathname.startsWith(path));
    const selectedKey = activeIndex !== -1 ? String(activeIndex + 1) : '1';

    if (isLoggedIn) {
        return (
            <Header style={{ display: 'flex', alignItems: 'center', backgroundColor: HomeColors.colors.color3, position: 'sticky', top: 0, zIndex: 1000, width: '100%' }}>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                    <Menu
                        theme="dark"
                        mode="horizontal"
                        items={items1}
                        selectedKeys={[selectedKey]}
                        style={{ flex: 1, minWidth: 0, backgroundColor: HomeColors.colors.color3 }}
                    />
                </div>

                <GlobalChatNotification />
                
                {isAdmin && (
                    <div style={{ marginRight: 24, cursor: 'pointer' }} onClick={() => setAdminSugerimentsVisible(true)}>
                        <Badge count={unreadCount} overflowCount={99}>
                            <BsBell style={{ color: 'white', fontSize: 20 }} />
                        </Badge>
                    </div>
                )}

                {isImpersonating && (
                    <Button
                        type="primary"
                        danger
                        onClick={handleStopImpersonation}
                        style={{ marginRight: 16 }}
                    >
                        Volver a Admin
                    </Button>
                )}

                <UserProfileMenu />

                <AdminSugerimentsModal
                    visible={adminSugerimentsVisible}
                    onClose={() => setAdminSugerimentsVisible(false)}
                    onRead={() => setUnreadCount(0)}
                />
            </Header>
        );
    } else {
        return null;
    }
};

export default AppHeader;
